import { Link, useParams } from "react-router-dom";
import { FileText, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { servicesOptions } from "@/data/services";
import { zones } from "@/data/zones";
import NotFound from "@/pages/NotFound";

const ServiceDetail = () => {
  const { serviceSlug } = useParams();
  let service = null;
  for (let index = 0; index < servicesOptions.length; index += 1) {
    if (servicesOptions[index].slug === serviceSlug) {
      service = servicesOptions[index];
      break;
    }
  }

  if (!service) {
    return <NotFound />;
  }

  const related = [];
  for (let index = 0; index < servicesOptions.length; index += 1) {
    const item = servicesOptions[index];
    if (item.slug !== service.slug) {
      related.push(item);
    }
    if (related.length >= 3) break;
  }

  const priorityZones = [];
  for (let index = 0; index < zones.length; index += 1) {
    const zone = zones[index];
    if (zone.priority) {
      priorityZones.push(zone);
    }
    if (priorityZones.length >= 6) break;
  }

  const benefitItems = [];
  for (let index = 0; index < service.benefits.length; index += 1) {
    const benefit = service.benefits[index];
    benefitItems.push(<li key={benefit}>• {benefit}</li>);
  }

  const faqCards = [];
  for (let index = 0; index < service.faqs.length; index += 1) {
    const faq = service.faqs[index];
    faqCards.push(
      <div
        key={faq.q}
        className="border border-brand-blue/15 p-6 rounded-sm bg-white"
        data-testid={`service-detail-faq-${index}`}
      >
        <h3 className="font-semibold" data-testid={`service-detail-faq-question-${index}`}>
          {faq.q}
        </h3>
        <p className="text-sm text-brand-ink/70 mt-2" data-testid={`service-detail-faq-answer-${index}`}>
          {faq.a}
        </p>
      </div>,
    );
  }

  const zoneCards = [];
  for (let index = 0; index < priorityZones.length; index += 1) {
    const zone = priorityZones[index];
    zoneCards.push(
      <Link
        key={zone.slug}
        to={`/${zone.slug}`}
        className="border border-brand-blue/15 p-4 rounded-sm bg-white hover:border-brand-red"
        data-testid={`service-detail-zone-${zone.slug}`}
      >
        <p className="font-semibold" data-testid={`service-detail-zone-name-${zone.slug}`}>
          {zone.name}
        </p>
        <p className="text-sm text-brand-ink/70" data-testid={`service-detail-zone-desc-${zone.slug}`}>
          {zone.description}
        </p>
      </Link>,
    );
  }

  const relatedCards = [];
  for (let index = 0; index < related.length; index += 1) {
    const item = related[index];
    let relatedBenefits = "";
    for (let j = 0; j < item.benefits.length; j += 1) {
      relatedBenefits = relatedBenefits ? `${relatedBenefits} • ${item.benefits[j]}` : item.benefits[j];
    }
    relatedCards.push(
      <Link
        key={item.slug}
        to={`/services/${item.slug}`}
        className="border border-white/20 p-6 rounded-sm"
        data-testid={`service-detail-related-${item.slug}`}
      >
        <h3 className="font-heading text-xl uppercase" data-testid={`service-detail-related-title-${item.slug}`}>
          {item.title}
        </h3>
        <p className="text-sm text-white/80 mt-2" data-testid={`service-detail-related-desc-${item.slug}`}>
          {relatedBenefits}
        </p>
      </Link>,
    );
  }

  return (
    <div className="bg-white" data-testid="service-detail-page">
      <Seo
        title={`${service.title} | Fast Alu`}
        description={`${service.title} : ${service.intro}`}
      />
      <section className="bg-brand-blue/5" data-testid="service-detail-hero">
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10">
          <div>
            <p className="text-xs uppercase tracking-wider text-brand-red" data-testid="service-detail-kicker">
              Service dédié
            </p>
            <h1 className="font-heading text-4xl uppercase" data-testid="service-detail-title">
              {service.title}
            </h1>
            <p className="text-brand-ink/70 mt-4" data-testid="service-detail-intro">
              {service.intro}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="tel:0184160307"
                className="bg-brand-red text-white px-5 py-3 rounded-full uppercase text-xs font-bold"
                data-testid="service-detail-call"
              >
                <Phone size={14} /> Appelez maintenant
              </a>
              <Link
                to="/contact"
                className="border border-brand-blue/40 text-brand-blue px-5 py-3 rounded-full uppercase text-xs font-bold"
                data-testid="service-detail-quote"
              >
                <FileText size={14} /> Demander un devis
              </Link>
            </div>
            <ul className="mt-6 space-y-2 text-sm text-brand-ink/70" data-testid="service-detail-benefits">
              {benefitItems}
            </ul>
          </div>
          <div>
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-72 object-cover rounded-sm"
              data-testid="service-detail-image"
            />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="service-detail-details">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="border border-brand-blue/15 p-6 rounded-sm bg-white" data-testid="service-detail-card-1">
            <h3 className="font-heading text-xl uppercase">Diagnostic rapide</h3>
            <p className="text-sm text-brand-ink/70 mt-2">
              Nos équipes analysent la situation et proposent une solution adaptée à votre urgence.
            </p>
          </div>
          <div className="border border-brand-blue/15 p-6 rounded-sm bg-white" data-testid="service-detail-card-2">
            <h3 className="font-heading text-xl uppercase">Intervention locale</h3>
            <p className="text-sm text-brand-ink/70 mt-2">
              Intervention rapide à Maisons-Alfort et dans tout le Val-de-Marne.
            </p>
          </div>
          <div className="border border-brand-blue/15 p-6 rounded-sm bg-white" data-testid="service-detail-card-3">
            <h3 className="font-heading text-xl uppercase">Devis transparent</h3>
            <p className="text-sm text-brand-ink/70 mt-2">
              Un devis clair, des conseils précis, et un suivi personnalisé.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-brand-blue/5" data-testid="service-detail-faq">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl uppercase" data-testid="service-detail-faq-title">
            Questions fréquentes
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mt-6">{faqCards}</div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="service-detail-zones">
        <h2 className="font-heading text-3xl uppercase" data-testid="service-detail-zones-title">
          Zones prioritaires
        </h2>
        <div className="grid md:grid-cols-3 gap-4 mt-6">{zoneCards}</div>
      </section>

      <section className="bg-brand-blue" data-testid="service-detail-related">
        <div className="max-w-6xl mx-auto px-6 py-16 text-white">
          <h2 className="font-heading text-3xl uppercase" data-testid="service-detail-related-title">
            Services associés
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-6">{relatedCards}</div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
